import React from 'react';
import PropTypes from 'prop-types';

/**
 * Enhanced NoDataMessage component for displaying engaging messages when no data is available
 * 
 * @param {Object} props - Component props
 * @param {string} [props.message] - The message to display
 * @param {string} [props.subMessage] - Optional secondary message with more details
 * @param {string} [props.icon] - Icon name or component to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} [props.action] - Optional action button or link
 * @param {string} [props.style] - Visual style ('default', 'minimal', 'card')
 * @returns {JSX.Element} Rendered component
 */
const NoDataMessage = ({
  message = 'No data available',
  subMessage = 'Data may be loading or not yet available for this selection.',
  icon = '📊',
  className = '',
  action,
  style = 'default'
}) => {
  // Different style variations
  const styles = {
    default: 'py-12 px-6 bg-gray-800 border border-gray-700 rounded-lg',
    minimal: 'py-6',
    card: 'py-10 px-6 bg-gray-800/50 border border-gray-700/50 rounded-lg shadow-inner'
  };
  
  return (
    <div className={`flex flex-col items-center justify-center text-center ${styles[style] || styles.default} ${className}`}>
      <div className="text-5xl mb-5 opacity-75">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-200 mb-2 font-sora">{message}</h3>
      {subMessage && (
        <p className="text-sm text-gray-400 mb-4 max-w-sm mx-auto font-inter">{subMessage}</p>
      )}
      {action && (
        <div className="mt-4">
          {action}
        </div>
      )}
    </div>
  );
};

NoDataMessage.propTypes = {
  message: PropTypes.string,
  subMessage: PropTypes.string,
  icon: PropTypes.node,
  className: PropTypes.string,
  action: PropTypes.node,
  style: PropTypes.oneOf(['default', 'minimal', 'card'])
};

export default NoDataMessage; 